/**
 * RegimeShading — lightweight-charts v5 series primitive
 *
 * Colors the area fill BELOW the equity curve based on each day's regime.
 * Each day has a regime from the HMM (stored in market_regimes table).
 * The area under the line changes color: green (favorable), amber (cautious), red (adverse).
 * White dashed vertical lines mark regime transitions.
 */

import type {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  Time,
  IChartApiBase,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

type MarketRegime = 'favorable' | 'cautious' | 'adverse';

export interface RegimeHistoryEntry {
  tradingDate: string;
  regime: MarketRegime;
  probability: number;
}

interface ChartDataPoint {
  time: number;
  value: number;
}

export interface RegimePnLPeriod {
  startDate: string;
  endDate: string;
  regime: MarketRegime;
  netPnl: number;
}

const REGIME_COLORS: Record<MarketRegime, { top: string; bottom: string }> = {
  favorable: {
    top: 'rgba(34, 197, 94, 0.50)',
    bottom: 'rgba(34, 197, 94, 0.02)',
  },
  cautious: {
    top: 'rgba(251, 191, 36, 0.40)',
    bottom: 'rgba(251, 191, 36, 0.02)',
  },
  adverse: {
    top: 'rgba(220, 38, 38, 0.50)',
    bottom: 'rgba(220, 38, 38, 0.02)',
  },
};

/**
 * Build a map from date string → regime, plus a sorted list for nearest-lookup
 */
function buildRegimeMap(entries: RegimeHistoryEntry[]): {
  map: Map<string, MarketRegime>;
  sorted: RegimeHistoryEntry[];
} {
  const map = new Map<string, MarketRegime>();
  for (const e of entries) {
    map.set(e.tradingDate, e.regime);
  }
  // Keep sorted ascending for nearest-previous lookup
  const sorted = [...entries].sort((a, b) => a.tradingDate.localeCompare(b.tradingDate));
  return { map, sorted };
}

/**
 * Look up regime for a date, falling back to nearest previous trading day
 * (handles weekends, holidays, and gaps in regime data)
 */
function lookupRegime(
  dateStr: string,
  map: Map<string, MarketRegime>,
  sorted: RegimeHistoryEntry[],
): MarketRegime {
  const exact = map.get(dateStr);
  if (exact) return exact;
  // Find nearest previous date
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (sorted[i].tradingDate <= dateStr) return sorted[i].regime;
  }
  return sorted.length > 0 ? sorted[0].regime : 'cautious';
}

function timestampToDateStr(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
}

// ── Renderer ───────────────────────────────────────────────────────

class RegimeShadingRenderer implements IPrimitivePaneRenderer {
  private _regimeMap: Map<string, MarketRegime>;
  private _regimeSorted: RegimeHistoryEntry[];
  private _chart: IChartApiBase<Time>;
  private _series: ISeriesApi<SeriesType, Time>;
  private _chartData: ChartDataPoint[];
  private _pnlPeriods: RegimePnLPeriod[];

  constructor(
    regimeMap: Map<string, MarketRegime>,
    regimeSorted: RegimeHistoryEntry[],
    chart: IChartApiBase<Time>,
    series: ISeriesApi<SeriesType, Time>,
    chartData: ChartDataPoint[],
    pnlPeriods: RegimePnLPeriod[],
  ) {
    this._regimeMap = regimeMap;
    this._regimeSorted = regimeSorted;
    this._chart = chart;
    this._series = series;
    this._chartData = chartData;
    this._pnlPeriods = pnlPeriods;
  }

  draw(): void {}

  drawBackground(target: CanvasRenderingTarget2D): void {
    target.useMediaCoordinateSpace(({ context, mediaSize }) => {
      const timeScale = this._chart.timeScale();
      const data = this._chartData;
      if (data.length < 2) return;

      // Convert all data points to screen coordinates with regime and date
      const points: { x: number; y: number; regime: MarketRegime; dateStr: string }[] = [];
      for (const point of data) {
        const px = timeScale.timeToCoordinate(point.time as unknown as Time);
        const py = this._series.priceToCoordinate(point.value);
        if (px === null || py === null) continue;

        const dateStr = timestampToDateStr(point.time);
        const regime = lookupRegime(dateStr, this._regimeMap, this._regimeSorted);
        points.push({ x: px as number, y: py as number, regime, dateStr });
      }

      if (points.length < 2) return;

      // Draw area segments between consecutive points, colored by regime
      // Group consecutive same-regime points into segments for smooth fills
      let segStart = 0;
      for (let i = 1; i <= points.length; i++) {
        const isEnd = i === points.length;
        const regimeChanged = !isEnd && points[i].regime !== points[segStart].regime;

        if (isEnd || regimeChanged) {
          // Draw this segment's area fill
          const segEnd = isEnd ? i - 1 : i;
          const segPoints = points.slice(segStart, segEnd + 1);
          const regime = points[segStart].regime;
          const colors = REGIME_COLORS[regime];

          if (segPoints.length >= 2) {
            const minY = Math.min(...segPoints.map(p => p.y));
            const gradient = context.createLinearGradient(0, minY, 0, mediaSize.height);
            gradient.addColorStop(0, colors.top);
            gradient.addColorStop(1, colors.bottom);

            context.beginPath();
            context.moveTo(segPoints[0].x, segPoints[0].y);
            for (let j = 1; j < segPoints.length; j++) {
              context.lineTo(segPoints[j].x, segPoints[j].y);
            }
            context.lineTo(segPoints[segPoints.length - 1].x, mediaSize.height);
            context.lineTo(segPoints[0].x, mediaSize.height);
            context.closePath();

            context.fillStyle = gradient;
            context.fill();
          }

          // Draw white dashed vertical line at regime transition
          if (regimeChanged) {
            const transitionX = (points[i - 1].x + points[i].x) / 2;
            context.strokeStyle = 'rgba(255, 255, 255, 0.18)';
            context.lineWidth = 1;
            context.setLineDash([4, 4]);
            context.beginPath();
            context.moveTo(transitionX, 0);
            context.lineTo(transitionX, mediaSize.height);
            context.stroke();
            context.setLineDash([]);
          }

          segStart = isEnd ? i : i;
        }
      }

      // Draw P&L labels in the vertical middle of each regime zone
      // Match P&L periods to visible chart points by date string
      for (const period of this._pnlPeriods) {
        if (period.netPnl === 0) continue;

        // Find points that fall within this P&L period (by date string comparison)
        const periodPoints = points.filter(
          p => p.dateStr >= period.startDate && p.dateStr <= period.endDate
        );
        if (periodPoints.length === 0) continue;

        const x1 = periodPoints[0].x;
        const x2 = periodPoints[periodPoints.length - 1].x;
        const centerX = (x1 + x2) / 2;
        const spanWidth = x2 - x1;

        // Only show label if the zone is wide enough
        if (spanWidth < 40) continue;

        // Place label at vertical midpoint between line and bottom
        const lineY = periodPoints.reduce((sum, p) => sum + p.y, 0) / periodPoints.length;
        const avgY = (lineY + mediaSize.height) / 2;

        const pnlText = (period.netPnl >= 0 ? '+' : '-') +
          '$' + Math.abs(Math.round(period.netPnl)).toLocaleString();

        const isPositive = period.netPnl >= 0;

        // Draw background pill for readability
        context.font = 'bold 11px -apple-system, BlinkMacSystemFont, sans-serif';
        const metrics = context.measureText(pnlText);
        const padX = 4, padY = 3;
        context.fillStyle = 'rgba(0, 0, 0, 0.55)';
        context.beginPath();
        const rx = centerX - metrics.width / 2 - padX;
        const ry = avgY - 6 - padY;
        const rw = metrics.width + padX * 2;
        const rh = 12 + padY * 2;
        context.roundRect(rx, ry, rw, rh, 4);
        context.fill();

        // Draw text
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = isPositive
          ? 'rgba(74, 222, 128, 1)'
          : 'rgba(248, 113, 113, 1)';
        context.fillText(pnlText, centerX, avgY);
        context.textBaseline = 'alphabetic';
      }
    });
  }
}

// ── Pane View ──────────────────────────────────────────────────────

class RegimeShadingPaneView implements IPrimitivePaneView {
  private _primitive: RegimeShadingPrimitive;

  constructor(primitive: RegimeShadingPrimitive) {
    this._primitive = primitive;
  }

  zOrder(): 'bottom' {
    return 'bottom';
  }

  renderer(): IPrimitivePaneRenderer | null {
    const { chart, series, regimeMap, regimeSorted, chartData, pnlPeriods } = this._primitive;
    if (!chart || !series || regimeMap.size === 0 || chartData.length === 0) return null;
    return new RegimeShadingRenderer(regimeMap, regimeSorted, chart, series, chartData, pnlPeriods);
  }
}

// ── Series Primitive ───────────────────────────────────────────────

export class RegimeShadingPrimitive implements ISeriesPrimitive<Time> {
  private _chart: IChartApiBase<Time> | null = null;
  private _series: ISeriesApi<SeriesType, Time> | null = null;
  private _requestUpdate: (() => void) | null = null;
  private _regimeMap: Map<string, MarketRegime> = new Map();
  private _regimeSorted: RegimeHistoryEntry[] = [];
  private _chartData: ChartDataPoint[] = [];
  private _pnlPeriods: RegimePnLPeriod[] = [];
  private _paneViews: IPrimitivePaneView[];

  constructor() {
    this._paneViews = [new RegimeShadingPaneView(this)];
  }

  get chart() { return this._chart; }
  get series() { return this._series; }
  get regimeMap() { return this._regimeMap; }
  get regimeSorted() { return this._regimeSorted; }
  get chartData() { return this._chartData; }
  get pnlPeriods() { return this._pnlPeriods; }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._chart = param.chart;
    this._series = param.series as ISeriesApi<SeriesType, Time>;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return this._paneViews;
  }

  updateRegimes(entries: RegimeHistoryEntry[]): void {
    const { map, sorted } = buildRegimeMap(entries);
    this._regimeMap = map;
    this._regimeSorted = sorted;
    this._requestUpdate?.();
  }

  updateChartData(data: ChartDataPoint[]): void {
    this._chartData = data;
    this._requestUpdate?.();
  }

  updatePnLPeriods(periods: RegimePnLPeriod[]): void {
    this._pnlPeriods = periods;
    this._requestUpdate?.();
  }
}
